import { migratePresetModel } from "@defibrillator/preset-core";
import { AuditionNote, AuditionPlan, MAX_AUDITION_SECONDS, } from "@defibrillator/renderer-protocol";
import { measureBuffer } from "./measure.js";
import { applyModulationMatrix } from "./modulation.js";
export const APPROXIMATION_NOTICE = "Approximate offline preview — not Vital-accurate. Measurements are advisory only.";
export const PREVIEW_RENDERER_VERSION = "2026.08.09.2";
export const PREVIEW_SAMPLE_RATE = 44_100;
/** Fallback total length when a custom phrase is not provided. */
export const PREVIEW_DURATION_SECONDS = 3.5;
function midiToHz(note) {
    return 440 * 2 ** ((note - 69) / 12);
}
function shapeFromWavetable(wt) {
    if (wt.kind === "basic") {
        return { shape: wt.shape, pulseWidth: 0.25 };
    }
    if (wt.kind === "pulse") {
        return { shape: "pulse", pulseWidth: wt.width };
    }
    return { shape: "harmonic", pulseWidth: 0.25 };
}
function oscSample(phase, shape, pulseWidth = 0.25) {
    const t = ((phase % 1) + 1) % 1;
    switch (shape) {
        case "sine":
            return Math.sin(2 * Math.PI * t);
        case "triangle":
            return 1 - 4 * Math.abs(t - 0.5);
        case "square":
            return t < 0.5 ? 1 : -1;
        case "pulse":
            return t < pulseWidth ? 1 : -1;
        case "noise":
            return Math.sin(t * 12.9898 + phase * 78.233) * 2 - 1;
        case "harmonic":
            return Math.sin(2 * Math.PI * t) + 0.35 * Math.sin(4 * Math.PI * t);
        case "saw":
        default:
            return 2 * t - 1;
    }
}
/** Per-note ADSR against a gate length (seconds). */
function noteEnvelope(localT, gate, attack, decay, sustain, release) {
    if (localT < 0)
        return 0;
    const a = Math.max(0.001, attack);
    const d = Math.max(0.001, decay);
    const r = Math.max(0.001, release);
    const noteOff = Math.max(a * 0.5, gate);
    if (localT < a) {
        return localT / a;
    }
    if (localT < a + d) {
        const x = (localT - a) / d;
        return 1 - x * (1 - sustain);
    }
    if (localT < noteOff) {
        return sustain;
    }
    const relT = (localT - noteOff) / r;
    if (relT >= 1) {
        return 0;
    }
    return sustain * (1 - relT);
}
export function selectAuditionMode(model) {
    if (model.sequences.length > 0 || model.style === "sequence")
        return "held_note";
    if (model.style === "perc")
        return "percussion";
    return "phrase";
}
export function listUnsupportedInPreview(model) {
    const missing = [];
    if (model.params.chorus_on >= 0.5)
        missing.push("chorus");
    if (model.params.phaser_on >= 0.5)
        missing.push("phaser");
    if (model.params.flanger_on >= 0.5)
        missing.push("flanger");
    if (model.params.distortion_on >= 0.5)
        missing.push("distortion");
    if (model.params.compressor_on >= 0.5)
        missing.push("compressor");
    if (model.params.osc1_spectral_morph_amount > 0.05)
        missing.push("spectral_morph");
    if (model.params.sub_on >= 0.5)
        missing.push("sub_oscillator_exact");
    return missing;
}
/**
 * Build a short ascending phrase. Gate/spacing stretch with attack so slow
 * pads still open; release adds a short tail after the last note.
 */
export function buildPreviewPhrase(model) {
    const attack = Math.max(0, model.params.env1_attack);
    const release = Math.max(0, model.params.env1_release);
    const style = (model.style ?? "").toLowerCase();
    const mode = selectAuditionMode(model);
    if (mode === "held_note") {
        const durationSeconds = Math.min(MAX_AUDITION_SECONDS, Math.max(2.5, 4));
        return {
            notes: [{ midi: 60, start: 0, duration: Math.min(durationSeconds * 0.92, durationSeconds) }],
            durationSeconds,
        };
    }
    if (mode === "percussion") {
        const notes = [0, 0.18, 0.4, 0.85].map((start, i) => ({
            midi: i % 2 === 0 ? 36 : 42,
            start,
            duration: 0.12,
        }));
        return { notes, durationSeconds: Math.min(MAX_AUDITION_SECONDS, 2.2) };
    }
    const gate = Math.max(0.28, Math.min(1.35, attack * 0.9 + 0.32));
    const step = gate * (style.includes("pad") || style.includes("ambient") ? 0.72 : 0.88);
    const degrees = style.includes("bass")
        ? [36, 43, 48, 55, 60]
        : style.includes("pluck") || style.includes("lead")
            ? [67, 69, 71, 74, 79]
            : [60, 64, 67, 71, 72];
    const notes = degrees.map((midi, i) => ({
        midi,
        start: i * step,
        duration: i === degrees.length - 1 ? gate * 1.55 : gate,
    }));
    const last = notes[notes.length - 1];
    const releaseTail = Math.min(1.4, Math.max(0.18, release * 0.4));
    const durationSeconds = Math.min(MAX_AUDITION_SECONDS, last.start + last.duration + releaseTail);
    return { notes, durationSeconds };
}
function mixOscillators(oscs, baseHz, sampleRate) {
    let mix = 0;
    let levelSum = 0;
    for (const osc of oscs) {
        if (osc.level <= 0)
            continue;
        const voices = Math.max(1, Math.min(8, Math.round(osc.unison)));
        for (let u = 0; u < voices; u++) {
            const spread = voices === 1 ? 0 : (u / (voices - 1) - 0.5) * 2;
            const hz = baseHz * 2 ** (osc.transpose / 12) * 2 ** ((spread * osc.detune * 0.35) / 12);
            const phaseInc = hz / sampleRate;
            const phase = (osc.phase + u * 0.07) % 1;
            mix += oscSample(phase, osc.shape, osc.pulseWidth) * (osc.level / voices);
            osc.phase = (osc.phase + phaseInc) % 1;
        }
        levelSum += osc.level;
    }
    if (levelSum > 0) {
        mix /= Math.max(1, levelSum);
    }
    return mix;
}
/**
 * Synthesize a short mono Float32 buffer from PresetModel using simple
 * oscillators + ADSR + modulation matrix + one-pole low-pass.
 */
export function renderApproximate(rawModel, options) {
    const model = migratePresetModel(rawModel);
    const sampleRate = options?.sampleRate ?? PREVIEW_SAMPLE_RATE;
    const auditionMode = options?.auditionMode ?? selectAuditionMode(model);
    let notes;
    let durationSeconds;
    if (options?.phrase?.length) {
        notes = options.phrase;
        durationSeconds = options.durationSeconds ?? options.phrase.reduce((a, n) => Math.max(a, n.start + n.duration), 0);
        if (!Number.isFinite(durationSeconds) || durationSeconds > MAX_AUDITION_SECONDS) {
            throw new Error("Explicit audition duration is invalid or exceeds maximum");
        }
    }
    else if (options?.midiNote != null) {
        durationSeconds = options.durationSeconds ?? PREVIEW_DURATION_SECONDS;
        durationSeconds = Math.min(durationSeconds, MAX_AUDITION_SECONDS);
        notes = [{ midi: options.midiNote, start: 0, duration: Math.min(durationSeconds * 0.75, MAX_AUDITION_SECONDS) }];
    }
    else if (auditionMode === "held_note") {
        const held = buildPreviewPhrase({ ...model, style: "sequence" });
        notes = held.notes;
        durationSeconds = Math.min(options?.durationSeconds ?? held.durationSeconds, MAX_AUDITION_SECONDS);
    }
    else {
        const built = buildPreviewPhrase(model);
        notes = built.notes;
        durationSeconds = Math.min(options?.durationSeconds ?? built.durationSeconds, MAX_AUDITION_SECONDS);
    }
    const n = Math.max(1, Math.floor(sampleRate * durationSeconds));
    const samples = new Float32Array(n);
    const wt1 = shapeFromWavetable(model.wavetables[1]);
    const wt2 = shapeFromWavetable(model.wavetables[2]);
    const wt3 = shapeFromWavetable(model.wavetables[3]);
    const voices = notes.map(() => [
        {
            level: model.params.osc1_level,
            transpose: model.params.osc1_transpose,
            shape: wt1.shape,
            pulseWidth: wt1.pulseWidth,
            phase: model.params.osc1_phase,
            unison: model.params.osc1_unison_voices,
            detune: model.params.osc1_unison_detune,
        },
        {
            level: model.params.osc2_level,
            transpose: model.params.osc2_transpose,
            shape: wt2.shape,
            pulseWidth: wt2.pulseWidth,
            phase: model.params.osc2_phase,
            unison: model.params.osc2_unison_voices,
            detune: model.params.osc2_unison_detune,
        },
        {
            level: model.params.osc3_level,
            transpose: model.params.osc3_transpose,
            shape: wt3.shape,
            pulseWidth: wt3.pulseWidth,
            phase: model.params.osc3_phase,
            unison: model.params.osc3_unison_voices,
            detune: model.params.osc3_unison_detune,
        },
    ]);
    const filterOn = model.params.filter_on >= 0.5;
    let lp = 0;
    const attack = model.params.env1_attack;
    const decay = model.params.env1_decay;
    const sustain = model.params.env1_sustain;
    const release = model.params.env1_release;
    for (let i = 0; i < n; i++) {
        const t = i / sampleRate;
        let mix = 0;
        let active = 0;
        for (let v = 0; v < notes.length; v++) {
            const note = notes[v];
            const localT = t - note.start;
            const env = noteEnvelope(localT, note.duration, attack, decay, sustain, release);
            if (env <= 0)
                continue;
            const modulated = applyModulationMatrix(model, t, note.start);
            const oscs = voices[v];
            oscs[0].level = modulated.osc1Level;
            oscs[1].level = modulated.osc2Level;
            oscs[2].level = modulated.osc3Level;
            oscs[0].transpose = modulated.osc1Transpose;
            oscs[1].transpose = modulated.osc2Transpose;
            oscs[2].transpose = modulated.osc3Transpose;
            const midi = note.midi + modulated.voiceTranspose;
            const tone = mixOscillators(oscs, midiToHz(midi), sampleRate);
            let sample = tone * env;
            if (filterOn) {
                const cutoff = Math.min(20_000, Math.max(20, modulated.cutoff));
                const alpha = 1 - Math.exp((-2 * Math.PI * cutoff) / sampleRate);
                lp += alpha * (sample - lp);
                sample = lp;
            }
            mix += sample * Math.max(0.05, modulated.volume / Math.max(0.05, model.params.volume));
            active += 1;
        }
        if (active > 1) {
            mix /= Math.sqrt(active);
        }
        let out = mix * model.params.volume;
        if (model.params.reverb_on >= 0.5) {
            const delayed = samples[Math.max(0, i - Math.floor(sampleRate * 0.03))] ?? 0;
            out += delayed * 0.2 * model.params.reverb_dry_wet;
        }
        if (model.params.delay_on >= 0.5) {
            const delayed = samples[Math.max(0, i - Math.floor(sampleRate * 0.12))] ?? 0;
            out += delayed * 0.25 * model.params.delay_dry_wet;
        }
        samples[i] = out;
    }
    return {
        samples,
        sampleRate,
        channels: 1,
        approximation: true,
        notice: APPROXIMATION_NOTICE,
        measurements: measureBuffer(samples),
        auditionMode,
        previewRendererVersion: PREVIEW_RENDERER_VERSION,
        unsupportedInPreview: listUnsupportedInPreview(model),
    };
}
//# sourceMappingURL=render.js.map